import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import Card from '../common/Card';
import Button from '../common/Button';
import FormSection from './FormSection';
import Spinner from '../common/Spinner';
import { summaryReportService } from '../../services/summaryReportService';
import { patientService } from '../../services/patientService';
import Input from '../common/Input';
import Select from '../common/Select';
import { format } from 'date-fns';
import { uploadImageToS3 } from '../../utils/uploadImageToS3';
import { requestPDF } from '../../services/pdfService';
import PublishingOverlay from '../common/PublishingOverlay';
import { generateSummaryReportHTML } from '../../services/summaryReportService';
import { generatePDFFilename } from '../../utils/generatePDFFilename';

const SummaryReportForm = () => {
  const navigate = useNavigate();
  const { id, patientId } = useParams();
  const appointmentId = patientId;
  
  console.log('SummaryReportForm mounted - patientId:', patientId, 'id:', id);
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [publishingStatus, setPublishingStatus] = useState(null);
  const [patient, setPatient] = useState(null);
  const [reportId, setReportId] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentSection, setCurrentSection] = useState(0);
  const [fetchingRecommendations, setFetchingRecommendations] = useState(false);
  const [fetchProgress, setFetchProgress] = useState(0);
  const [secondsRemaining, setSecondsRemaining] = useState(30);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [isEditingAllowed, setIsEditingAllowed] = useState(true);
  const [biomarkersLoading, setBiomarkersLoading] = useState(false);
  const [biomarkersLoaded, setBiomarkersLoaded] = useState(false);
  
  const [formData, setFormData] = useState({
    patient_id: '',
    patient_name: '',
    patient_gender: '',
    patient_age: '',
    exam_date: format(new Date(), 'yyyy-MM-dd'),
    
    // Overview section
    biomarkers_tested: '',
    biomarkers_in_range: '',
    biomarkers_of_concern: '',
    overview_summary: '',
    overview_description: '',
    
    // Goals section
    goals_status: {
      goal_name: '',
      biomarker_status: [
        { biomarker_name: 'VO2 MAX', current: '', percentile: '', goal: '' },
        { biomarker_name: 'BODY FAT', current: '', percentile: '', goal: '' },
        { biomarker_name: 'AEROBIC THRESHOLD', current: '', percentile: '', goal: '' },
        { biomarker_name: 'INSULIN', current: '', percentile: '', goal: '' },
        { biomarker_name: 'HOMA IR', current: '', percentile: '', goal: '' }
      ]
    },
    
    // Tactics sections
    nutrition_tactics: [{ tactic_id: '', name: '', description: '', biomarkers: [] }],
    exercise_tactics: [{ tactic_id: '', name: '', description: '', biomarkers: [] }],
    supplementation_tactics: [{ tactic_id: '', name: '', description: '', biomarkers: [] }],
    lifestyle_tactics: [{ tactic_id: '', name: '', description: '', biomarkers: [] }],
    
    // Additional sections
    recommendations: '',
    notes: '',
    
    pdf_url: ''
  });

  // Helper function to calculate age from DOB
  const calculateAgeFromDOB = (dob) => {
    if (!dob) return null;
    const birthDate = new Date(dob);
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  // Helper function to normalize gender
  const normalizeGender = (gender) => {
    if (!gender) return '';
    const genderLower = gender.toLowerCase().trim();
    if (genderLower === 'male' || genderLower === 'm') return 'Male';
    if (genderLower === 'female' || genderLower === 'f') return 'Female';
    if (genderLower === 'other') return 'Other';
    return gender; // Return as-is if doesn't match
  };

  const sections = [
    {
      label: 'Patient Information',
      fields: [
        { name: 'patient_name', label: 'Patient Name', type: 'text', required: true },
        { name: 'patient_age', label: 'Age', type: 'number', required: true },
        { name: 'patient_gender', label: 'Gender', type: 'select', options: ['Male', 'Female', 'Other'], required: true },
        { name: 'exam_date', label: 'Exam Date', type: 'date', required: true }
      ]
    },
    {
      label: 'Overview',
      fields: [
        { name: 'biomarkers_tested', label: 'Biomarkers Tested', type: 'number', required: true },
        { name: 'biomarkers_in_range', label: 'Biomarkers In Range', type: 'number', required: true },
        { name: 'biomarkers_of_concern', label: 'Biomarkers of Concern', type: 'number', required: true },
        { name: 'overview_summary', label: 'Summary', type: 'textarea', required: true },
        { name: 'overview_description', label: 'Description', type: 'textarea', required: true }
      ]
    },
    {
      label: 'Goals',
      component: 'goals_status'
    },
    {
      label: 'Nutrition',
      component: 'nutrition_tactics'
    },
    {
      label: 'Exercise',
      component: 'exercise_tactics'
    },
    {
      label: 'Supplementation',
      component: 'supplementation_tactics'
    },
    {
      label: 'Lifestyle',
      component: 'lifestyle_tactics'
    },
    {
      label: 'Recommendations & Notes',
      fields: [
        { name: 'recommendations', label: 'Recommendations', type: 'textarea', required: false },
        { name: 'notes', label: 'Notes', type: 'textarea', required: false }
      ]
    }
  ];

  // Store biomarkers goals data for fallback
  const [biomarkersGoalsData, setBiomarkersGoalsData] = useState(null);

  // Fetch biomarkers data from API
  useEffect(() => {
    const fetchBiomarkers = async () => {
      if (!appointmentId) return;
      
      try {
        setBiomarkersLoading(true);
        console.log('Fetching biomarkers for appointmentId:', appointmentId);
        
        const response = await fetch(
          `https://vitaldev.vitalinsights.in/admin/biomarkers/by-appointment/${appointmentId}`,
          {
            method: 'GET',
            headers: {
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            }
          }
        );
        
        if (!response.ok) {
          throw new Error(`Failed to fetch biomarkers: ${response.status}`);
        }
        
        const result = await response.json();
        console.log('Biomarkers response:', result);
        
        if (result.success && result.data) {
          const { total_biomarkers, in_range_count, abnormal_count, goal_name, biomarker_status } = result.data;
          
          // Store goals data from biomarkers API for fallback
          if (goal_name && biomarker_status) {
            setBiomarkersGoalsData({
              goal_name,
              biomarker_status
            });
            console.log('Stored biomarkers goals data:', { goal_name, biomarker_status });
          }
          
          // Only set as loaded if we have at least one valid value
          const hasValidData = total_biomarkers || in_range_count || abnormal_count;
          
          setFormData(prev => ({
            ...prev,
            biomarkers_tested: total_biomarkers || '',
            biomarkers_in_range: in_range_count || '',
            biomarkers_of_concern: abnormal_count || ''
          }));
          
          if (hasValidData) {
            setBiomarkersLoaded(true);
            console.log('Biomarkers data loaded successfully');
          } else {
            setBiomarkersLoaded(false);
            console.log('API returned empty biomarker data');
          }
        }
      } catch (error) {
        console.error('Error fetching biomarkers:', error);
        // Don't show error toast - just allow user to edit
        setBiomarkersLoaded(false);
      } finally {
        setBiomarkersLoading(false);
      }
    };

    fetchBiomarkers();
  }, [appointmentId]);

  useEffect(() => {
    const loadPatientData = async () => {
      try {
        if (appointmentId) {
          console.log('Loading patient data for appointmentId:', appointmentId);
          const response = await patientService.getPatientById(appointmentId);
          console.log('Patient data response:', response);
          
          // Extract data from response (API wraps it: response.data contains {status, success, message, data: {...}})
          const patientData = response.data?.data || response.data || response;
          console.log('Extracted patient data:', patientData);
          console.log('sukra_order_id:', patientData?.sukra_order_id);
          console.log('sukra_branch_id:', patientData?.sukra_branch_id);
          
          setPatient(patientData);
          
          // Extract and process patient information
          const profileData = patientData.profile || {};
          const patientName = profileData.name || '';
          const patientGender = normalizeGender(profileData.gender);
          
          // Determine age: use profile.age if available, else calculate from DOB
          let patientAge = profileData.age;
          if (!patientAge && profileData.dob) {
            patientAge = calculateAgeFromDOB(profileData.dob);
          }
          
          setFormData(prev => ({
            ...prev,
            patient_id: patientData.id || '',
            patient_name: patientName,
            patient_gender: patientGender,
            patient_age: patientAge || ''
          }));
          
          // Fetch existing summary report data by appointment ID
          try {
            console.log('Attempting to fetch existing report for appointmentId:', appointmentId);
            const reportData = await summaryReportService.getReportByAppointmentId(appointmentId);
            console.log('Report data response:', reportData);
            
            if (reportData && reportData.data) {
              // Set report ID and edit mode
              setReportId(reportData.data.id);
              setIsEditMode(true);
              console.log('Existing report found, setting edit mode with reportId:', reportData.data.id);
              
              // Ensure goals_status maintains its structure even if null/undefined from API
              // Use biomarkers goals data as fallback if report doesn't have goals_status
              const defaultGoalsStatus = biomarkersGoalsData || {
                goal_name: '',
                biomarker_status: [
                  { biomarker_name: 'VO2 MAX', current: '', percentile: '', goal: '' },
                  { biomarker_name: 'BODY FAT', current: '', percentile: '', goal: '' },
                  { biomarker_name: 'AEROBIC THRESHOLD', current: '', percentile: '', goal: '' },
                  { biomarker_name: 'INSULIN', current: '', percentile: '', goal: '' },
                  { biomarker_name: 'HOMA IR', current: '', percentile: '', goal: '' }
                ]
              };
              
              const reportDataWithDefaults = {
                ...reportData.data,
                goals_status: reportData.data.goals_status || defaultGoalsStatus
              };
              
              setFormData(prev => ({
                ...prev,
                ...reportDataWithDefaults
              }));
            } else {
              console.log('No report data found in response');
              setIsEditMode(false);
              
              // If no report exists, use biomarkers goals data as fallback
              if (biomarkersGoalsData) {
                console.log('Using biomarkers goals data as fallback');
                setFormData(prev => ({
                  ...prev,
                  goals_status: biomarkersGoalsData
                }));
              }
            }
          } catch (reportError) {
            // If no report exists, just continue with empty form
            console.log('No existing summary report found or error fetching:', reportError.message);
            setIsEditMode(false);
            
            // Use biomarkers goals data as fallback if report fetch fails
            if (biomarkersGoalsData) {
              console.log('Using biomarkers goals data as fallback (from catch)');
              setFormData(prev => ({
                ...prev,
                goals_status: biomarkersGoalsData
              }));
            }
          }
        } else {
          console.log('No appointmentId provided');
        }
      } catch (error) {
        console.error('Error loading data:', error);
        toast.error('Failed to load patient data');
      } finally {
        console.log('Setting loading to false');
        setLoading(false);
      }
    };

    loadPatientData();
  }, [appointmentId, biomarkersGoalsData]);

  // Countdown timer for fetch recommendations
  useEffect(() => {
    if (!fetchingRecommendations) {
      setSecondsRemaining(30);
      setElapsedSeconds(0);
      setFetchProgress(0);
      return;
    }

    // Start countdown from 30 seconds, track elapsed time
    const interval = setInterval(() => {
      setElapsedSeconds(prev => prev + 1);
      
      setSecondsRemaining(prev => {
        if (prev <= 1) return 0; // Reach 0
        return prev - 1;
      });
      
      // Update progress bar proportionally (30 seconds = 100%)
      setFetchProgress(prev => {
        if (prev >= 100) return 100;
        return prev + (100 / 30); // Increment to reach 100% in 30 seconds
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [fetchingRecommendations]);

  // Complete progress when fetch finishes
  useEffect(() => {
    if (!fetchingRecommendations && fetchProgress > 0) {
      setFetchProgress(100);
      const timer = setTimeout(() => {
        setFetchProgress(0);
        setSecondsRemaining(30);
        setElapsedSeconds(0);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [fetchingRecommendations]);

  const handleFormChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleTacticsChange = (tacticType, index, field, value) => {
    setFormData(prev => {
      const tactics = [...prev[tacticType]];
      tactics[index] = {
        ...tactics[index],
        [field]: value
      };
      return {
        ...prev,
        [tacticType]: tactics
      };
    });
  };

  const addTactic = (tacticType) => {
    setFormData(prev => ({
      ...prev,
      [tacticType]: [...prev[tacticType], { tactic_id: '', name: '', description: '', biomarkers: [] }]
    }));
  };

  const removeTactic = (tacticType, index) => {
    setFormData(prev => ({
      ...prev,
      [tacticType]: prev[tacticType].filter((_, i) => i !== index)
    }));
  };

  const handleGoalChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      goals_status: {
        ...prev.goals_status,
        [field]: value
      }
    }));
  };

  const handleBiomarkerChange = (bioIndex, field, value) => {
    setFormData(prev => {
      const biomarkers = [...prev.goals_status.biomarker_status];
      biomarkers[bioIndex] = {
        ...biomarkers[bioIndex],
        [field]: value
      };
      return {
        ...prev,
        goals_status: {
          ...prev.goals_status,
          biomarker_status: biomarkers
        }
      };
    });
  };


  const renderField = (field) => {
    const value = formData[field.name];
    
    // Check if this is a biomarker field - only disable if data was successfully loaded AND has a value
    const isBiomarkerField = ['biomarkers_tested', 'biomarkers_in_range', 'biomarkers_of_concern'].includes(field.name);
    const isBiomarkerFieldDisabled = isBiomarkerField && biomarkersLoaded && value;

    if (field.type === 'textarea') {
      return (
        <div key={field.name} className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {field.label} {field.required && <span className="text-red-500">*</span>}
          </label>
          <textarea
            value={value || ''}
            onChange={(e) => handleFormChange(field.name, e.target.value)}
            className="w-full p-2 border rounded"
            rows="4"
            placeholder={`Enter ${field.label.toLowerCase()}`}
          />
        </div>
      );
    }

    if (field.type === 'select') {
      return (
        <div key={field.name} className="mb-4">
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {field.label} {field.required && <span className="text-red-500">*</span>}
          </label>
          <select
            value={value || ''}
            onChange={(e) => handleFormChange(field.name, e.target.value)}
            className="w-full p-2 border rounded"
          >
            <option value="">Select {field.label}</option>
            {field.options?.map(opt => (
              <option key={opt} value={opt}>{opt}</option>
            ))}
          </select>
        </div>
      );
    }

    return (
      <div key={field.name} className="mb-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {field.label} {field.required && <span className="text-red-500">*</span>}
          {isBiomarkerFieldDisabled && <span className="text-xs text-gray-500 ml-2">(Auto-fetched)</span>}
        </label>
        <div className="relative">
          <input
            type={field.type}
            value={value || ''}
            onChange={(e) => !isBiomarkerFieldDisabled && handleFormChange(field.name, e.target.value)}
            disabled={isBiomarkerFieldDisabled}
            className={`w-full p-2 border rounded ${
              isBiomarkerFieldDisabled 
                ? 'bg-gray-100 text-gray-700 cursor-not-allowed border-gray-300' 
                : ''
            }`}
            placeholder={`Enter ${field.label.toLowerCase()}`}
          />
          {isBiomarkerField && biomarkersLoading && (
            <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
              <div className="animate-spin h-4 w-4 border-2 border-orange-500 border-t-transparent rounded-full"></div>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderTacticsSection = (tacticType) => {
    const tactics = formData[tacticType];

    return (
      <div className="space-y-4">
        {tactics.map((tactic, index) => (
          <div key={index} className="p-4 border rounded bg-gray-50">
            <div className="flex justify-between items-center mb-3">
              <h4 className="font-medium">Tactic {index + 1}</h4>
              {tactics.length > 1 && (
                <button
                  onClick={() => removeTactic(tacticType, index)}
                  className="text-red-500 hover:text-red-700 text-sm"
                >
                  Remove
                </button>
              )}
            </div>
            
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Tactic Name
              </label>
              <input
                type="text"
                value={tactic.name || ''}
                onChange={(e) => handleTacticsChange(tacticType, index, 'name', e.target.value)}
                className="w-full p-2 border rounded"
                placeholder="Enter tactic name"
              />
            </div>
            
            <div className="mb-3">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                value={tactic.description || ''}
                onChange={(e) => handleTacticsChange(tacticType, index, 'description', e.target.value)}
                className="w-full p-2 border rounded"
                rows="3"
                placeholder="Enter tactic description"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Impacted Biomarkers (comma-separated)
              </label>
              <input
                type="text"
                value={Array.isArray(tactic.biomarkers) ? tactic.biomarkers.join(', ') : ''}
                onChange={(e) => handleTacticsChange(tacticType, index, 'biomarkers', e.target.value.split(',').map(b => b.trim()))}
                className="w-full p-2 border rounded"
                placeholder="e.g., Albumin, Cholesterol, Glucose"
              />
            </div>
          </div>
        ))}
        
        <Button
          variant="secondary"
          onClick={() => addTactic(tacticType)}
          className="w-full"
        >
          Add Another Tactic
        </Button>
      </div>
    );
  };

  const renderGoalsSection = () => {
    const goal = formData.goals_status;

    if (!goal) return null;

    return (
      <div className="space-y-6">
        <div className="p-4 border rounded bg-gray-50">
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Goal Name
            </label>
            <input
              type="text"
              value={goal.goal_name || ''}
              onChange={(e) => handleGoalChange('goal_name', e.target.value)}
              className="w-full p-2 border rounded"
              placeholder="Enter goal name (e.g., Weight Loss)"
            />
          </div>

          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Biomarker Status
            </label>
            <div className="overflow-x-auto">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="border p-2 text-left text-sm font-medium">Biomarker</th>
                    <th className="border p-2 text-center text-sm font-medium">Current</th>
                    <th className="border p-2 text-center text-sm font-medium">% (Percentile)</th>
                    <th className="border p-2 text-center text-sm font-medium">Goal</th>
                  </tr>
                </thead>
                <tbody>
                  {goal.biomarker_status.map((biomarker, bioIndex) => (
                    <tr key={bioIndex} className="hover:bg-gray-50">
                      <td className="border p-2 text-sm text-gray-700">
                        {biomarker.biomarker_name || `Biomarker ${bioIndex + 1}`}
                      </td>
                      <td className="border p-2">
                        <input
                          type="text"
                          value={biomarker.current || ''}
                          onChange={(e) => handleBiomarkerChange(bioIndex, 'current', e.target.value)}
                          className="w-full p-1 border rounded text-sm text-center"
                          placeholder="46.4"
                        />
                      </td>
                      <td className="border p-2">
                        <input
                          type="number"
                          value={biomarker.percentile || ''}
                          onChange={(e) => handleBiomarkerChange(bioIndex, 'percentile', e.target.value)}
                          className="w-full p-1 border rounded text-sm text-center"
                          placeholder="60"
                          min="0"
                          max="100"
                        />
                      </td>
                      <td className="border p-2">
                        <input
                          type="text"
                          value={biomarker.goal || ''}
                          onChange={(e) => handleBiomarkerChange(bioIndex, 'goal', e.target.value)}
                          className="w-full p-1 border rounded text-sm text-center"
                          placeholder="50"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  };

  const handlePreview = async () => {
    try {
      const html = await generateSummaryReportHTML(formData);
      sessionStorage.setItem('summaryReportData', JSON.stringify(formData));
      
      // Open preview in new tab
      const previewUrl = `/summary-report/${appointmentId}/preview`;
      window.open(previewUrl, '_blank');
    } catch (error) {
      console.error('Preview error:', error);
      toast.error('Failed to generate preview');
    }
  };

  const handleSubmit = async (status) => {
    setSubmitting(true);
    let pdf_url = null;
    
    try {
      // If status is 'published', generate PDF first
      if (status === 'published') {
        setPublishingStatus('generating');
        try {
          // Generate HTML for the report
          const html = await generateSummaryReportHTML(formData);
          
          // Convert HTML to PDF using the pdfService
          const pdfBlob = await requestPDF(html);
          
          // Create a URL for the PDF blob
          const pdfObjectUrl = URL.createObjectURL(pdfBlob);
          
          // Upload the PDF to S3
          const patientName = patient?.profile?.name || formData.patient_name || patient?.name || 'patient';
          const orderId = patient?.sukra_order_id || patient?.id || 'unknown';
          const fileName = generatePDFFilename(patientName, 'summary', orderId);
          console.log('Uploading PDF with filename:', fileName);
          
          try {
            // Create a File object from the Blob
            const pdfFile = new File([pdfBlob], fileName, { type: 'application/pdf' });
            console.log('Created PDF file object:', pdfFile);
            
            // Create FormData for direct upload
            const uploadFormData = new FormData();
            uploadFormData.append('file', pdfFile);
            uploadFormData.append('file_name', fileName);
            
            // Get token for authentication
            const token = localStorage.getItem('token');
            
            // Upload directly to backend
            console.log('Uploading file with token:', token ? 'Token exists' : 'No token');
            
            const uploadRes = await fetch('https://vitaldev.vitalinsights.in/user/upload-file-to-s3', {
              method: 'POST',
              body: uploadFormData,
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });

            console.log('Upload response:', JSON.stringify(uploadRes));
            
            if (!uploadRes.ok) {
              const errorText = await uploadRes.text();
              console.error('Upload error response:', errorText);
              throw new Error(`Upload failed with status ${uploadRes.status}: ${errorText}`);
            }
            
            const response = await uploadRes.json();
            console.log('Upload response:', JSON.stringify(response));
            console.log('Response keys:', Object.keys(response));
            
            // Try multiple possible response keys
            const fileUrl = response.fileUrl || response.file_url || response.url || response.data?.fileUrl || response.data?.file_url;
            
            if (!fileUrl) {
              console.error('No file URL found in response. Full response:', response);
              throw new Error('Invalid response from server - no file URL');
            }
            
            // Set the PDF URL - ensure it's a string
            pdf_url = String(fileUrl);
            console.log('Successfully set PDF URL:', pdf_url);
          } catch (uploadError) {
            console.error('Error during PDF upload:', uploadError);
            
            // Fallback to object URL if upload fails
            pdf_url = pdfObjectUrl;
            console.warn('Using object URL as fallback:', pdf_url);
          }
          
          console.log('PDF generated and uploaded:', pdf_url);
        } catch (error) {
          console.error('Error generating PDF:', error);
          setPublishingStatus('error');
          toast.error('Failed to generate PDF');
          setSubmitting(false);
          return;
        }
      }
      
      // Fetch all tactics to match with manually entered tactics
      console.log('Fetching tactics to match with form data before submission...');
      const allTactics = await summaryReportService.fetchAllTactics();
      console.log('Fetched tactics for matching:', allTactics.length);
      
      // Helper function to enrich tactics with tactic_id if they match database tactics
      const enrichTacticsWithIds = (tactics) => {
        return tactics.map(tactic => {
          // Skip empty tactics
          if (!tactic.name || tactic.name.trim() === '') {
            return tactic;
          }
          
          // If tactic already has an ID, keep it
          if (tactic.tactic_id) {
            return tactic;
          }
          
          // Try to find matching tactic in database by name
          const matchedTactic = allTactics.find(dbTactic => 
            dbTactic.tactic_name === tactic.name
          );
          
          if (matchedTactic) {
            console.log(`✓ Matched tactic: "${tactic.name}" with ID: ${matchedTactic.id || matchedTactic._id}`);
            return {
              ...tactic,
              tactic_id: matchedTactic.id || matchedTactic._id
            };
          } else {
            console.warn(`✗ No match found for tactic: "${tactic.name}"`);
            return {
              ...tactic,
              tactic_id: '' // Keep empty for consistency
            };
          }
        });
      };
      
      // Enrich all tactics with IDs
      const enrichedNutritionTactics = enrichTacticsWithIds(formData.nutrition_tactics || []);
      const enrichedExerciseTactics = enrichTacticsWithIds(formData.exercise_tactics || []);
      const enrichedSupplementationTactics = enrichTacticsWithIds(formData.supplementation_tactics || []);
      const enrichedLifestyleTactics = enrichTacticsWithIds(formData.lifestyle_tactics || []);
      
      console.log('Enriched tactics summary:', {
        nutrition: enrichedNutritionTactics.filter(t => t.tactic_id).length + '/' + enrichedNutritionTactics.length,
        exercise: enrichedExerciseTactics.filter(t => t.tactic_id).length + '/' + enrichedExerciseTactics.length,
        supplementation: enrichedSupplementationTactics.filter(t => t.tactic_id).length + '/' + enrichedSupplementationTactics.length,
        lifestyle: enrichedLifestyleTactics.filter(t => t.tactic_id).length + '/' + enrichedLifestyleTactics.length
      });
      
      // Create the submission data
      const payload = {
        ...formData,
        report_status: status,
        appointment_id: appointmentId,
        // Ensure goals_status is always included
        goals_status: formData.goals_status || {
          goal_name: '',
          biomarker_status: [
            { biomarker_name: 'VO2 MAX', current: '', percentile: '', goal: '' },
            { biomarker_name: 'BODY FAT', current: '', percentile: '', goal: '' },
            { biomarker_name: 'AEROBIC THRESHOLD', current: '', percentile: '', goal: '' },
            { biomarker_name: 'INSULIN', current: '', percentile: '', goal: '' },
            { biomarker_name: 'HOMA IR', current: '', percentile: '', goal: '' }
          ]
        },
        // Use enriched tactics with IDs
        nutrition_tactics: enrichedNutritionTactics,
        exercise_tactics: enrichedExerciseTactics,
        supplementation_tactics: enrichedSupplementationTactics,
        lifestyle_tactics: enrichedLifestyleTactics
      };
      
      // If we have a PDF URL, include it in the submission
      if (pdf_url) {
        console.log('Setting PDF URL in submission:', pdf_url);
        payload.pdf_url = pdf_url;
        console.log('Final payload with PDF URL:', JSON.stringify(payload));
        console.log('PDF URL in payload:', payload.pdf_url);
      } else if (status === 'published') {
        console.warn('No PDF URL available for submission');
      }

      console.log('About to submit with payload:', JSON.stringify(payload));
      console.log('Goals status in payload:', payload.goals_status);
      console.log('Tactics in payload:', {
        nutrition_tactics: payload.nutrition_tactics,
        exercise_tactics: payload.exercise_tactics,
        supplementation_tactics: payload.supplementation_tactics,
        lifestyle_tactics: payload.lifestyle_tactics
      });
      
      // Always use POST API for submission (same as VO2Max)
      console.log('Creating/Submitting report via POST');
      const response = await summaryReportService.createReport(payload);
      console.log('Create report response:', response);
      toast.success('Report submitted successfully');

      if (status === 'published') {
        setPublishingStatus('success');
        setTimeout(() => {
          setPublishingStatus(null);
          setSubmitting(false);
          navigate('/patients');
        }, 2000);
      } else {
        setSubmitting(false);
        navigate('/patients');
      }
    } catch (error) {
      console.error('Submit error:', error);
      toast.error('Failed to submit report');
      setSubmitting(false);
      if (status === 'published') {
        setPublishingStatus('error');
      }
    }
  };

  const handleCancel = () => {
    navigate('/patients');
  };

  const handleSectionChange = (index) => {
    setCurrentSection(index);
  };

  const goToNextSection = () => {
    if (currentSection < sections.length - 1) {
      setCurrentSection(currentSection + 1);
    }
  };

  const goToPreviousSection = () => {
    if (currentSection > 0) {
      setCurrentSection(currentSection - 1);
    }
  };

  const handleFetchRecommendations = async () => {
    try {
      setFetchingRecommendations(true);
      
      // Get patient data to extract sid_no and branch_id
      console.log('Patient data:', patient);
      
      if (!patient) {
        toast.error('Patient information not found');
        setFetchingRecommendations(false);
        return;
      }
      
      // Extract sid_no from patient profile or use patient id
      const sidNo = patient.sukra_order_id;
      const branchId = patient.sukra_branch_id;
      const visitDate = patient.slot_start_time?.split("T")[0] || format(new Date(), 'yyyy-MM-dd');
      
      console.log('Fetching recommendations with:', { sidNo, visitDate, branchId });
      
      if (!sidNo || !branchId) {
        toast.error('Missing required patient information (sid or branch)');
        setFetchingRecommendations(false);
        return;
      }
      
      // Fetch recommendations from external API
      const response = await summaryReportService.fetchRecommendations(sidNo, visitDate, branchId);
      console.log('Recommendations response:', response);
      
      // Check if data came from API or fallback
      if (response.source === 'fallback' && response.error) {
        // Show error message with API details
        let errorMsg = `API failed`;
        if (response.error.statusCode) {
          errorMsg += ` with status code ${response.error.statusCode}`;
        }
        if (response.error.detail) {
          errorMsg += `: ${response.error.detail}`;
        } else if (response.error.message) {
          errorMsg += `: ${response.error.message}`;
        }
        errorMsg += `. Loading sample data as fallback for test.`;
        toast.warning(errorMsg);
      } else {
        // Data came from API
        toast.success('Recommendations loaded successfully');
      }
      
      // Fetch all tactics
      const allTactics = await summaryReportService.fetchAllTactics();
      console.log('All tactics fetched:', allTactics.length, 'tactics');
      console.log('Sample tactic names from DB:', allTactics.slice(0, 5).map(t => t.tactic_name));
      
      // Match recommendations with tactics
      const matchedTactics = matchRecommendationsWithTactics(response.data, allTactics);
      console.log('Matched tactics result:', matchedTactics);
      console.log('Nutrition tactics count:', matchedTactics.nutrition_tactics.length);
      console.log('Exercise tactics count:', matchedTactics.exercise_tactics.length);
      console.log('Supplementation tactics count:', matchedTactics.supplementation_tactics.length);
      console.log('Lifestyle tactics count:', matchedTactics.lifestyle_tactics.length);
      
      // Update form data with matched tactics - APPEND instead of replace
      // Only add tactics if they have data, skip empty arrays
      setFormData(prev => ({
        ...prev,
        nutrition_tactics: matchedTactics.nutrition_tactics.length > 0
          ? [...prev.nutrition_tactics.filter(t => t.name), ...matchedTactics.nutrition_tactics]
          : prev.nutrition_tactics,
        exercise_tactics: matchedTactics.exercise_tactics.length > 0
          ? [...prev.exercise_tactics.filter(t => t.name), ...matchedTactics.exercise_tactics]
          : prev.exercise_tactics,
        supplementation_tactics: matchedTactics.supplementation_tactics.length > 0
          ? [...prev.supplementation_tactics.filter(t => t.name), ...matchedTactics.supplementation_tactics]
          : prev.supplementation_tactics,
        lifestyle_tactics: matchedTactics.lifestyle_tactics.length > 0
          ? [...prev.lifestyle_tactics.filter(t => t.name), ...matchedTactics.lifestyle_tactics]
          : prev.lifestyle_tactics,
        recommendations: matchedTactics.recommendations.length > 0
          ? prev.recommendations 
            ? `${prev.recommendations}\n${matchedTactics.recommendations.join('\n')}`
            : matchedTactics.recommendations.join('\n')
          : prev.recommendations
      }));
    } catch (error) {
      console.error('Error fetching recommendations:', error);
      toast.error('Failed to fetch recommendations');
    } finally {
      setFetchingRecommendations(false);
    }
  };

  const matchRecommendationsWithTactics = (recommendationsData, allTactics) => {
    const result = {
      nutrition_tactics: [],
      exercise_tactics: [],
      supplementation_tactics: [],
      lifestyle_tactics: [],
      recommendations: []
    };

    // Map API response keys to form data keys
    const categoryMap = {
      Nutrition: 'nutrition_tactics',
      Exercise: 'exercise_tactics',
      Supplement: 'supplementation_tactics',
      Lifestyle: 'lifestyle_tactics'
    };

    console.log('API Response keys:', Object.keys(recommendationsData));
    console.log('Recommendations data structure:', {
      Nutrition: recommendationsData.Nutrition?.length || 0,
      Exercise: recommendationsData.Exercise?.length || 0,
      Supplement: recommendationsData.Supplement?.length || 0,
      Lifestyle: recommendationsData.Lifestyle?.length || 0
    });

    // Process each category
    Object.entries(categoryMap).forEach(([apiKey, formKey]) => {
      const items = recommendationsData[apiKey] || [];
      console.log(`Processing ${apiKey} (${formKey}):`, items.length, 'items');
      
      // For all tactics, match with tactic names
      items.forEach(itemName => {
        if (itemName && itemName !== 'string') {
          // Find matching tactic by exact name match
          const matchedTactic = allTactics.find(tactic => tactic.tactic_name === itemName);
          
          console.log(`Searching for tactic: "${itemName}"`, matchedTactic ? 'FOUND' : 'NOT FOUND');
          
          if (matchedTactic) {
            // Extract biomarkers (can be objects or strings)
            const biomarkers = (matchedTactic.impacted_biomarkers || []).map(bm => 
              typeof bm === 'string' ? bm : bm.name
            );
            
            result[formKey].push({
              tactic_id: matchedTactic.id || matchedTactic._id,
              name: matchedTactic.tactic_name,
              description: matchedTactic.tactic_category || '',
              biomarkers: biomarkers
            });
          } else {
            // If no matching tactic found, create a tactic with the recommendation string as name
            console.warn(`No matching tactic found for: "${itemName}". Adding without tactic_id.`);
            result[formKey].push({
              tactic_id: '', // Add empty tactic_id for consistency
              name: itemName,
              description: '',
              biomarkers: []
            });
          }
        }
      });
    });

    return result;
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-full py-12">
        <Spinner size="lg" />
      </div>
    );
  }

  if (publishingStatus) {
    return <PublishingOverlay />;
  }

  return (
    <>
      {publishingStatus && (
        <PublishingOverlay status={publishingStatus} />
      )}
      
      {/* Full-page loader during recommendations fetch */}
      {fetchingRecommendations && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-8 flex flex-col items-center gap-6 w-96">
            <Spinner size="lg" />
            <div className="w-full">
              <p className="text-lg font-medium text-gray-800 mb-3">Fetching Recommendations...</p>
              <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-orange-400 to-orange-600 h-full rounded-full transition-all duration-300"
                  style={{ width: `${Math.min(fetchProgress, 100)}%` }}
                />
              </div>
              <p className="text-sm text-gray-600 mt-2 text-center font-semibold">
                {secondsRemaining > 0 ? `${secondsRemaining}s` : '0s'}
              </p>
              {elapsedSeconds > 30 && (
                <p className="text-xs text-orange-600 mt-2 text-center font-medium">
                  ⚠️ This is taking longer than expected. Please wait...
                </p>
              )}
            </div>
          </div>
        </div>
      )}
      
      <div className={`flex flex-col md:flex-row gap-4 p-4 ${fetchingRecommendations ? 'opacity-50 pointer-events-none' : ''}`}>
        <div className="md:w-64 flex-shrink-0">
          <div className="bg-white p-4 rounded-lg shadow space-y-6">
            {/* Patient Information */}
            {patient && (
              <div className="border-b pb-4">
                <h3 className="text-lg font-bold text-gray-800 mb-3">{patient.profile?.name}</h3>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">ID:</span>
                    <span className="font-medium">{patient.id}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Branch:</span>
                    <span className="font-medium">{patient.sukra_branch_id}</span>
                  </div>
                  {patient.slot_start_time && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Appointment:</span>
                      <span className="font-medium">{patient.slot_start_time?.split("T")[0]}</span>
                    </div>
                  )}
                </div>
              </div>
            )}
            
            {/* Report Sections */}
            <div>
              <h3 className="text-xl font-bold text-primary-500 mb-4">Report Sections</h3>
              <ul className="space-y-2">
                {sections.map((section, index) => (
                  <li key={index}>
                    <button
                      onClick={() => handleSectionChange(index)}
                      disabled={submitting}
                      className={`w-full text-left py-2 px-3 rounded transition ${currentSection === index 
                        ? 'bg-primary-500 text-white font-medium' 
                        : 'hover:bg-gray-100'}`}
                    >
                      {section.label}
                      {currentSection === index && (
                        <span className="float-right">→</span>
                      )}
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
        
        <div className="flex-1">
          <Card>
            <div className="flex justify-between items-center mb-6">
              <h1 className="text-2xl font-bold">
                {isEditMode ? 'Edit Summary Report' : 'New Summary Report'}
              </h1>
              
              {currentSection === 0 && (
                <Button
                  onClick={handleFetchRecommendations}
                  disabled={fetchingRecommendations || submitting}
                  className="bg-primary-500 hover:bg-primary-600 text-white"
                >
                  {fetchingRecommendations ? (
                    <div className="flex items-center gap-2">
                      <Spinner size="sm" />
                      Fetching...
                    </div>
                  ) : (
                    'Fetch Recommendations'
                  )}
                </Button>
              )}
            </div>
            
            <div className="mb-4">
              <h2 className="text-xl font-semibold">
                {sections[currentSection].label}
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Complete all fields in this section before proceeding
              </p>
            </div>

            <FormSection>
              <div className="space-y-6">
                {sections[currentSection].component === 'goals_status' ? (
                  renderGoalsSection()
                ) : sections[currentSection].component ? (
                  renderTacticsSection(sections[currentSection].component)
                ) : (
                  sections[currentSection].fields.map(field => renderField(field))
                )}
              </div>
            </FormSection>

            <div className="flex justify-between mt-6">
              <Button
                variant="secondary"
                onClick={currentSection > 0 ? goToPreviousSection : handleCancel}
              >
                {currentSection > 0 ? 'Previous' : 'Back to List'}
              </Button>

              {currentSection === sections.length - 1 ? (
                <div className="flex gap-2">
                  <Button
                    variant="secondary"
                    onClick={handlePreview}
                    disabled={submitting}
                  >
                    Preview Report
                  </Button>
                  <Button
                    onClick={() => handleSubmit('submitted')}
                    disabled={submitting}
                    variant="secondary"
                  >
                    {submitting ? 'Saving...' : 'Submit Report'}
                  </Button>
                  <Button
                    onClick={() => handleSubmit('published')}
                    disabled={submitting}
                    className="bg-primary-500 hover:bg-primary-600"
                  >
                    {submitting ? 'Publishing...' : 'Submit & Publish'}
                  </Button>
                </div>
              ) : (
                <Button onClick={goToNextSection}>Next</Button>
              )}
            </div>
          </Card>
        </div>
      </div>
    </>
  );
};

export default SummaryReportForm;
